import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';

function need(name,method){
  const x=g[name];
  if(!x||typeof x[method]!=='function') throw Error(name+'_'+method+'_REQUIRED');
  return x;
}
function state(x){
  return ['READY','REVIEW','BLOCK'].includes(x)?x:'REVIEW';
}
function worst(a,b){
  a=state(a); b=state(b);
  if(a==='BLOCK'||b==='BLOCK') return 'BLOCK';
  if(a==='REVIEW'||b==='REVIEW') return 'REVIEW';
  return 'READY';
}
function arr(x){return Array.isArray(x)?x:[];}

function create(opts={}){
  const packageFactory=need('ZooInvestCompanyResearchDataPackage','create');
  const compositionFactory=need('ZooInvestResearchPipelineCompositionRoot','create');
  const uiAdapter=need('ZooInvestResearchOutputUIFieldAdapter','build');

  const packageRuntime=packageFactory.create(opts.packageOptions||{});
  const researchRuntime=compositionFactory.create(opts.pipelineOptions||{});

  async function run(req={}){
    const symbol=String(req.symbol||'').trim().toUpperCase();
    if(!symbol) throw Error('SYMBOL_REQUIRED');

    const issues=[];
    let pkg=null;
    try{
      pkg=await packageRuntime.buildCompanyPackage({...req,symbol});
    }catch(e){
      issues.push({stage:'COMPANY_PACKAGE',severity:'REVIEW',message:String(e.message||e)});
      return Object.freeze({
        version:VERSION,symbol,status:'REVIEW',
        package:null,research:null,ui:null,issues:Object.freeze(issues)
      });
    }

    const researchInput=Object.freeze({
      symbol,
      scope:req.scope||'RESEARCH',
      companyResearch:pkg.companyResearch,
      sourceState:pkg.sourceState,
      providerResults:pkg.providerResults,
      packageIssues:pkg.issues,
      packageGuards:pkg.guards,

      /* Existing research engines only receive evidence-backed optional inputs
         when explicitly supplied. We do not fabricate thesis/evidence state. */
      changeCandidates:arr(req.changeCandidates),
      originalThesisPillars:arr(req.originalThesisPillars),
      currentThesisPillars:arr(req.currentThesisPillars),
      thesisVerification:req.thesisVerification||'UNVERIFIED',
      evidenceGraph:req.evidenceGraph||null,
      evidenceChanges:arr(req.evidenceChanges),
      researchTasks:arr(req.researchTasks),
      openGaps:arr(req.openGaps),
      evidenceConflicts:arr(req.evidenceConflicts),
      integrityMetrics:req.integrityMetrics||{},
      previousReadiness:req.previousReadiness||'REVIEW',
      readiness:req.readiness||'REVIEW',
      compoundSignal:req.compoundSignal||'NO_COMPOUND_SIGNAL',
      eventWithin24h:req.eventWithin24h===true,
      researchQueue:req.researchQueue||null,
      causeTransmission:arr(req.causeTransmission),
      companyTransmission:arr(req.companyTransmission),
      compoundDelta:arr(req.compoundDelta),
      evidenceFingerprint:req.evidenceFingerprint||null,
      thesisFingerprint:req.thesisFingerprint||null
    });

    let research=null;
    try{
      research=await researchRuntime.run(researchInput);
    }catch(e){
      issues.push({stage:'RESEARCH_RUNTIME',severity:'REVIEW',message:String(e.message||e)});
    }

    const packageState=state(pkg.status);
    const researchState=research?state(research.status):'REVIEW';
    const finalStatus=worst(packageState,researchState);

    const bridgeShape={
      symbol,
      status:finalStatus,
      package:pkg,
      researchInput,
      pipeline:research
    };

    let ui=null;
    try{
      ui=uiAdapter.build(bridgeShape);
    }catch(e){
      issues.push({stage:'UI_ADAPTER',severity:'REVIEW',message:String(e.message||e)});
    }

    return Object.freeze({
      version:VERSION,
      symbol,
      status:ui?finalStatus:worst(finalStatus,'REVIEW'),
      package:pkg,
      research,
      ui,
      researchInput,
      issues:Object.freeze(issues),
      execution:Object.freeze({
        providerToPackage:true,
        packageToConfiguredResearchPipeline:!!research,
        researchToUI:!!ui
      }),
      guards:Object.freeze({
        noUniversalScore:true,
        noTradeSignal:true,
        noCapitalPermission:true,
        noFabricatedThesis:true,
        noFabricatedEvidence:true,
        missingRemainsMissing:true
      })
    });
  }

  return Object.freeze({VERSION,run});
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  /* Stub only the three composition-level dependencies. Their own internals
     already have dedicated tests in prior releases. */
  const oldPkg=g.ZooInvestCompanyResearchDataPackage;
  const oldComp=g.ZooInvestResearchPipelineCompositionRoot;
  const oldUI=g.ZooInvestResearchOutputUIFieldAdapter;

  try{
    g.ZooInvestCompanyResearchDataPackage={
      create:()=>({
        async buildCompanyPackage(req){
          return {
            status:'READY',symbol:req.symbol,
            companyResearch:{
              canonicalFinancials:{symbol:req.symbol},
              financialHealth:{factualMetrics:{debtToEquity:.5,freeCashFlow:10,freeCashFlowMarginPct:10},sourceValues:{revenue:100}},
              financialHistory:{symbol:req.symbol,metrics:{}},
              growthTrajectory:{factual:{revenueCagrPct:12},signals:{revenueDirection:'UP'}},
              marketDaily:{rows:[{date:'2026-08-30',close:123}]}
            },
            sourceState:{fundamentals:{state:'VERIFIED'},market:{state:'VERIFIED'}},
            providerResults:[{status:'READY'}],issues:[],
            guards:{noUniversalScore:true}
          };
        }
      })
    };
    let captured=null;
    g.ZooInvestResearchPipelineCompositionRoot={
      create:()=>({
        async run(input){
          captured=input;
          return {
            status:'READY',
            snapshot:{fingerprint:'abc'},
            meaningfulChange:{meaningful:[]},
            thesisDrift:{state:'INSUFFICIENT_DATA'},
            integrity:{state:'INSUFFICIENT_DATA'},
            integrityGate:{gateState:'REVIEW'},
            priority:{symbol:input.symbol},
            brief:{symbol:input.symbol}
          };
        }
      })
    };
    g.ZooInvestResearchOutputUIFieldAdapter={
      build:run=>({symbol:run.symbol,runtimeStatus:run.status,ui:{dataBadges:{scoring:'UNCALIBRATED'}}})
    };

    const api=create();
    const r=await api.run({symbol:'aaa',scope:'OWNED'});
    ok('symbol normalized',r.symbol==='AAA');
    ok('package built',r.package.symbol==='AAA');
    ok('configured research pipeline executed',r.execution.packageToConfiguredResearchPipeline===true);
    ok('UI adapter executed',r.execution.researchToUI===true);
    ok('scope forwarded',captured.scope==='OWNED');
    ok('company research forwarded',captured.companyResearch.marketDaily.rows[0].close===123);
    ok('source state forwarded',captured.sourceState.fundamentals.state==='VERIFIED');
    ok('provider results forwarded',captured.providerResults.length===1);
    ok('missing thesis pillars stay empty',captured.originalThesisPillars.length===0&&captured.currentThesisPillars.length===0);
    ok('default thesis verification unverified',captured.thesisVerification==='UNVERIFIED');
    ok('missing evidence graph stays null',captured.evidenceGraph===null);
    ok('missing research tasks stay empty',captured.researchTasks.length===0);
    ok('missing gaps stay empty',captured.openGaps.length===0);
    ok('UI result preserved',r.ui.ui.dataBadges.scoring==='UNCALIBRATED');
    ok('no issues on clean execution',r.issues.length===0);
    ok('all three execution stages true',Object.values(r.execution).every(Boolean));
    ok('no fabricated thesis guard',r.guards.noFabricatedThesis===true);
    ok('no fabricated evidence guard',r.guards.noFabricatedEvidence===true);
    ok('missing remains missing guard',r.guards.missingRemainsMissing===true);

    const reviewPkg=g.ZooInvestCompanyResearchDataPackage;
    g.ZooInvestCompanyResearchDataPackage={
      create:()=>({async buildCompanyPackage(){return {
        status:'REVIEW',symbol:'BBB',companyResearch:{},sourceState:{},providerResults:[],issues:[{message:'MISSING'}],guards:{}
      }}})
    };
    const rr=create();
    const rv=await rr.run({symbol:'BBB'});
    ok('package review cannot become ready',rv.status==='REVIEW');
    g.ZooInvestCompanyResearchDataPackage=reviewPkg;

    let symbolErr=false;
    try{await api.run({symbol:''})}catch(e){symbolErr=e.message==='SYMBOL_REQUIRED'}
    ok('symbol required',symbolErr);

    const saved=g.ZooInvestCompanyResearchDataPackage;
    g.ZooInvestCompanyResearchDataPackage={create:()=>({async buildCompanyPackage(){throw Error('PKG_FAIL')}})};
    const failApi=create();
    const fr=await failApi.run({symbol:'FFF'});
    ok('package exception contained',fr.status==='REVIEW');
    ok('package exception recorded',fr.issues.some(x=>x.stage==='COMPANY_PACKAGE'));
    g.ZooInvestCompanyResearchDataPackage=saved;

    ok('no universal score on runtime',!('score' in api));
    ok('no trade signal on runtime',!('tradeSignal' in api));
    ok('no capital permission on runtime',!('capitalPermission' in api));

  } finally {
    g.ZooInvestCompanyResearchDataPackage=oldPkg;
    g.ZooInvestResearchPipelineCompositionRoot=oldComp;
    g.ZooInvestResearchOutputUIFieldAdapter=oldUI;
  }

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestEndToEndResearchRuntime=Object.freeze({VERSION,create,selfTest});
})(engineContext);